import { Avatar, Box, Container, Link, Stack, Typography } from '@mui/material';

import type {
  ConversationalFormNote,
  ConversationalFormOption,
  ConversationalFormPoint,
} from '@karehero/models';

import { Button } from '@/components/atoms/Button/Button';
import { Icon } from '@/components/atoms/Icon/Icon';
import { resolveIcon } from '@/components/atoms/Icon/resolveIcon';
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';

/** Pins the heading in one place on every interstitial. */
const HEADING_TOP = '120px';

const PICTURE_HEIGHT = '140px';

const POINT_ICON_SIZE = '40px';

/** Two large buttons and the gap between them, so the primary never moves. */
const ACTION_BAND_MIN_HEIGHT = '128px';

export interface ScreenLayoutProps {
  title: string;
  body?: string;
  /** The body as separate points, each beside its icon; replaces `body`. */
  points?: ConversationalFormPoint[];
  note?: ConversationalFormNote;
  /** A picture under the words, on the screens where one helps. */
  imageSrc?: string;
  options: ConversationalFormOption[];
  isSubmitting?: boolean;
  /** Omit to hide the back button. */
  onBack?: () => void;
  onChoose: (option: ConversationalFormOption) => void;
  onOpenLink?: (url: string) => void;
}

const Points = ({ points }: { points: ConversationalFormPoint[] }) => (
  <Stack component='ul' spacing={3} sx={{ m: 0, p: 0, pt: 2 }}>
    {points.map((point) => (
      <Stack
        component='li'
        key={point.text}
        direction='row'
        spacing={2}
        sx={{ alignItems: 'flex-start', listStyle: 'none' }}
      >
        <Avatar
          sx={{
            width: POINT_ICON_SIZE,
            height: POINT_ICON_SIZE,
            bgcolor: 'background.paper',
            color: 'custom.coral.600',
          }}
        >
          <Icon icon={resolveIcon(point.icon)} fontSize={18} />
        </Avatar>
        <Typography
          variant='subtitle1'
          sx={{ color: 'text.secondary', pt: 0.75 }}
        >
          {point.text}
        </Typography>
      </Stack>
    ))}
  </Stack>
);

/** ScreenLayout presents a step as a full-screen interstitial. */
export const ScreenLayout = ({
  title,
  body,
  points,
  note,
  imageSrc,
  options,
  isSubmitting,
  onBack,
  onChoose,
  onOpenLink,
}: ScreenLayoutProps) => (
  <ModalPageTemplate onBack={onBack}>
    <Container
      maxWidth='xs'
      disableGutters
      sx={{
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        px: 3,
        py: 2,
      }}
    >
      <Stack spacing={2} sx={{ flex: 1, pt: HEADING_TOP }}>
        <Typography variant='h1' align='center'>
          {title}
        </Typography>
        {points && <Points points={points} />}
        {body && !points && (
          <Typography
            variant='subtitle1'
            align='center'
            sx={{ color: 'text.secondary' }}
          >
            {body}
          </Typography>
        )}
        {note && (
          <Typography
            variant='subtitle1'
            align='center'
            sx={{ color: 'text.secondary', pt: points ? 2 : 1 }}
          >
            {note.lead}{' '}
            <Link
              component='button'
              type='button'
              variant='subtitle1'
              onClick={() => onOpenLink?.(note.linkUrl)}
            >
              {note.linkLabel}
            </Link>
          </Typography>
        )}
        {imageSrc && (
          <Box
            component='img'
            src={imageSrc}
            alt=''
            sx={{ height: PICTURE_HEIGHT, alignSelf: 'center', pt: 4 }}
          />
        )}
      </Stack>
      <Box sx={{ flexShrink: 0, minHeight: ACTION_BAND_MIN_HEIGHT, pb: 2 }}>
        <Stack spacing={2}>
          {options.map((option) => (
            <Button
              key={option.id}
              variant={option.isPrimary ? 'primary' : 'secondary'}
              size='lg'
              isFullWidth
              isDisabled={isSubmitting}
              ariaLabel={option.label}
              onPress={() => onChoose(option)}
            >
              {option.label}
            </Button>
          ))}
        </Stack>
      </Box>
    </Container>
  </ModalPageTemplate>
);

ScreenLayout.displayName = 'ScreenLayout';
export default ScreenLayout;
